import { VuexModule, getModule, Mutation, Action } from 'vuex-module-decorators'
import DynamicModule from '@/decorators/DynamicModule'
import ShipmentPointsGroup from '@/dto/deliveries/ShipmentPointsGroup'
import ShipmentPoint from '@/dto/deliveries/ShipmentPoint'
import ShipmentService from '@/services/ShipmentService'
import { NotifyHelper } from 'vue-kit'

export interface PointsGroups {
  [key: number]: ShipmentPointsGroup
}

@DynamicModule
class ShipmentStore extends VuexModule {
  public pointsGroups: PointsGroups | null = null
  public shipmentPointId: number | null = null

  get shipmentPoint(): ShipmentPoint | null {
    if (this.pointsGroups === null || this.shipmentPointId === null) {
      return null
    }

    return findShipmentPointById(this.pointsGroups, this.shipmentPointId)
  }

  get pointsGroupsAsArray(): ShipmentPointsGroup[] {
    return Object.values(this.pointsGroups || {})
  }

  get shipmentPoints(): ShipmentPoint[] {
    return getShipmentPoints(Object.values(this.pointsGroups || {}))
  }

  @Mutation
  public loadShipmentPoints(pointsGroups: PointsGroups) {
    this.pointsGroups = pointsGroups
  }

  @Mutation
  public selectShipmentPoint(shipmentPointId: number | null) {
    this.shipmentPointId = shipmentPointId
  }

  @Mutation
  private toggleShipmentPointStatus(shipmentPointId: number) {
    const shipmentPoint = findShipmentPointById(
      this.pointsGroups as PointsGroups,
      shipmentPointId
    ) as ShipmentPoint

    shipmentPoint.enabled = !shipmentPoint.enabled
  }

  @Mutation
  public removePointFromList(shipmentPointId: number) {
    const groups = this.pointsGroups as PointsGroups
    const shipmentPoint = findShipmentPointById(
      groups,
      shipmentPointId
    ) as ShipmentPoint

    if (shipmentPoint.id === this.shipmentPointId) {
      this.shipmentPointId = null
    }

    const pointGroup: ShipmentPointsGroup = groups[shipmentPoint.country.id]

    pointGroup.points.splice(
      pointGroup.points.findIndex((point: ShipmentPoint) => {
        return point.id === shipmentPointId
      }),
      1
    )

    if (pointGroup.points.length === 0) {
      delete groups[pointGroup.countryId]
      this.pointsGroups = { ...groups }
    }
  }

  @Action
  public async togglePointStatus(shipmentPointId: number) {
    return ShipmentService.togglePointStatus(shipmentPointId)
      .then(() => {
        NotifyHelper.success('Пункт отгрузки сохранен!')
        this.toggleShipmentPointStatus(shipmentPointId)
      })
      .catch(() =>
        NotifyHelper.error('Ошибка сервера. Обратитесь к разработчикам!')
      )
  }

  @Action({ rawError: true })
  public async removeShipmentPoint(shipmentPointId: number) {
    return ShipmentService.removeShipmentPoint(shipmentPointId)
      .then(() => {
        NotifyHelper.success('Пункт отгрузки удален!')
        this.removePointFromList(shipmentPointId)
      })
      .catch(error => {
        throw error.response.data.errors
      })
  }

  @Mutation
  private addShipmentPointToGroup(shipmentPoint: ShipmentPoint) {
    const countryId = shipmentPoint.country.id
    const groups = this.pointsGroups as PointsGroups

    if (countryId in groups) {
      groups[countryId].points.push(shipmentPoint)

      return
    }

    const newGroup: ShipmentPointsGroup = {
      countryId,
      countryName: shipmentPoint.country.name,
      points: [shipmentPoint],
    }

    this.pointsGroups = {
      ...groups,
      [countryId]: newGroup,
    }
  }

  @Action({ rawError: true })
  public async saveShipmentPoint({
    countryId,
    regionId,
    placeId,
  }: {
    countryId: number
    regionId: number
    placeId: number
  }) {
    return ShipmentService.saveShipmentPoint({ countryId, regionId, placeId })
      .then((shipmentPoint: ShipmentPoint) => {
        NotifyHelper.success('Пункт отгрузки добавлен!')
        this.addShipmentPointToGroup(shipmentPoint)

        return shipmentPoint
      })
      .catch(error => {
        throw error.response.data.errors
      })
  }
}

function findShipmentPointById(
  pointsGroups: PointsGroups,
  pointId: number
): ShipmentPoint | null {
  let shipmentPoint = null

  Object.values(pointsGroups).every((pointGroup: ShipmentPointsGroup) => {
    const currentPoint = pointGroup.points.find(
      (point: ShipmentPoint) => pointId === point.id
    )

    if (currentPoint) {
      shipmentPoint = currentPoint
      return false
    }

    return true
  })

  return shipmentPoint
}

function getShipmentPoints(pointsGroupsAsArray: ShipmentPointsGroup[]) {
  return pointsGroupsAsArray.reduce(
    (points: any[], shipmentPointGroup) => [
      ...points,
      ...shipmentPointGroup.points,
    ],
    []
  )
}

export default getModule(ShipmentStore)
